import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../hooks/useApiCall';
import { useQuery } from '../../hooks/useQuery';
import authService from '../../services/authService';

export default function useValidateRegister() {
  const [isLoading, setIsLoading] = useState(false);
  const [accessCode, setAccessCode] = useState('');

  const navigate = useNavigate();
  const { apiCall } = useApiCall();
  const query = useQuery();
  const userCode = query.get('codUsu');
  const userProfile = query.get('perfilUsu');
  const didntValidateAtFirstTime = query.has('sendCode');

  function handleAccessCodeChange(event: ChangeEvent<HTMLInputElement>) {
    setAccessCode(event.target.value);
  }

  const validateRegister = useCallback(async () => {
    if (!userCode || !userProfile) {
      toast.error('Não identificamos seu código de usuário ou perfil de usuário para validarmos seu acesso. Por favor, acesse o link novamente');
      return;
    }
    await apiCall({
      apiToCall: authService.validateRegister,
      isValidationToken: true,
      queryParams: { codAcesso: accessCode, codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível validar o acesso. Por favor, tente novamente (${response.mensagem})`);
          return;
        }
        if (!response.validado) {
          toast.error('Não foi possível validar as credenciais. Por favor, tente novamente');
          return;
        }
        toast.success('Cadastro validado com sucesso, por favor, realize seu login com cpf informado, ou nome de usuário para entrar no sistema!');
        navigate('/');
      },
      catchMessage: 'Não foi possível validar o acesso. Por favor, tente novamente',
    });
  }, [accessCode, apiCall, navigate, userCode, userProfile]);

  const resendAccessCode = useCallback(async () => {
    if (!userCode || !userProfile) {
      toast.error('Não identificamos seu código de usuário ou perfil de usuário para validarmos seu acesso. Por favor, acesse o link novamente');
      return;
    }
    await apiCall({
      apiToCall: authService.resendAccessCode,
      isValidationToken: true,
      queryParams: { codUsu: userCode, perfilUsu: userProfile },
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível reenviar o código de acesso em seu e-mail. Por favor, tente novamente (${response.mensagem})`);
          navigate('/');
          return;
        }
        if (!response.validado) {
          toast.error('Não foi possível validar as credenciais. Por favor, tente novamente');
          navigate('/');
          return;
        }
        toast.info('Acabamos de lhe enviar um novo e-mail com seu código de validação. Por favor, use-o para validar seu cadastro. ATENÇÃO: Se atente a usar o código enviado neste novo e-mail, e não o código enviado em e-mails anteriores.');
      },
      catchMessage: 'Não foi possível reenviar o código de acesso em seu e-mail. Por favor, tente novamente'
    });
  }, [apiCall, navigate, userCode, userProfile]);

  useEffect(() => {
    if (!didntValidateAtFirstTime) {
      toast.info('Lhe enviamos um e-mail com um código de validação. Por favor, use-o para validar seu cadastro.');
      return;
    }
    resendAccessCode();
  }, [didntValidateAtFirstTime, resendAccessCode]);

  return {
    isLoading,
    accessCode,
    handleAccessCodeChange,
    validateRegister,
  };
}
